/**
 * Shared NetKeep domain types.
 *
 * All monetary amounts and rates are transported as decimal strings
 * (e.g. "12.50", "13.6") so they never pass through binary floating point.
 * Percentages are expressed in percent units: "13.6" means 13.6 %.
 */

/** A non-negative base-10 decimal encoded as a string, e.g. "19.99". */
export type DecimalString = string;

/** ISO 4217 currency code, e.g. "USD". */
export type CurrencyCode = string;

// ---------------------------------------------------------------------------
// Fee rules
// ---------------------------------------------------------------------------

/** Components of a sale that can make up the amount a fee is charged on. */
export const BASIS_COMPONENTS = ["ITEM_PRICE", "BUYER_SHIPPING", "SALES_TAX"] as const;
export type BasisComponent = (typeof BASIS_COMPONENTS)[number];

export const FEE_CATEGORIES = ["MARKETPLACE", "PAYMENT_PROCESSING", "PROMOTED_LISTING", "OTHER"] as const;
export type FeeCategory = (typeof FEE_CATEGORIES)[number];

/**
 * Marginal percentage tier. The percent applies only to the slice of the
 * basis that falls inside this tier (like income-tax brackets).
 * `upTo` is the inclusive upper bound of the slice; `null` means unbounded.
 * Tiers must be in ascending order and the last tier must be unbounded.
 */
export interface PercentTier {
  upTo: DecimalString | null;
  percent: DecimalString;
}

/**
 * Threshold-selected fixed fee. The first tier whose `upTo` is greater than
 * or equal to the full basis amount supplies the fixed fee.
 * Tiers must be in ascending order and the last tier must be unbounded.
 */
export interface FixedTier {
  upTo: DecimalString | null;
  amount: DecimalString;
}

export interface FeeRule {
  /** Stable machine code, unique within a schedule (e.g. "FINAL_VALUE"). */
  code: string;
  label: string;
  category: FeeCategory;
  /** Sale components summed to form the amount this fee is charged on. */
  basis: BasisComponent[];
  /** Flat percent of the basis. Mutually exclusive with `percentTiers`. */
  percent?: DecimalString;
  /** Marginal percent tiers. Mutually exclusive with `percent`. */
  percentTiers?: PercentTier[];
  /** Flat fixed fee per transaction. Mutually exclusive with `fixedTiers`. */
  fixedAmount?: DecimalString;
  /** Threshold-selected fixed fee. Mutually exclusive with `fixedAmount`. */
  fixedTiers?: FixedTier[];
  /** Lower bound applied to the computed fee when the basis is above zero. */
  minimumFee?: DecimalString;
  /** Upper bound (cap) applied to the computed fee. */
  maximumFee?: DecimalString;
}

// ---------------------------------------------------------------------------
// Fee profiles and schedules
// ---------------------------------------------------------------------------

/** Who collects and remits sales tax for the transaction. */
export const TAX_COLLECTORS = ["MARKETPLACE", "SELLER", "NONE"] as const;
export type TaxCollector = (typeof TAX_COLLECTORS)[number];

export const TRANSACTION_TYPES = ["SHIPPED", "LOCAL_PICKUP", "INVOICE", "IN_PERSON"] as const;
export type TransactionType = (typeof TRANSACTION_TYPES)[number];

export const VERIFICATION_STATUSES = ["VERIFIED", "UNVERIFIED", "STALE", "FLAGGED"] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUSES)[number];

export const SCHEDULE_STATUSES = ["DRAFT", "ACTIVE", "SUPERSEDED", "REJECTED"] as const;
export type ScheduleStatus = (typeof SCHEDULE_STATUSES)[number];

/**
 * Everything the fee engine needs to price one combination of
 * selling platform + payment method + transaction type.
 *
 * A profile is the *complete* set of fees for that combination. The engine
 * never merges two profiles, which is how double-charging (e.g. eBay fees
 * plus PayPal fees on a single eBay sale) is prevented structurally.
 */
export interface FeeProfile {
  id: string;
  name: string;
  currency: CurrencyCode;
  /** Default tax handling for this channel; the user may override per sale. */
  taxCollectedBy: TaxCollector;
  /**
   * True when payment processing is bundled into the marketplace fee
   * (e.g. a platform-managed checkout). Adding a manual payment fee on such
   * a profile produces a warning.
   */
  paymentProcessingIncluded: boolean;
  /** Sale components a promoted-listing (advertising) rate is applied to. */
  promotedListingBasis: BasisComponent[];
  rules: FeeRule[];
}

export interface PlatformRef {
  slug: string;
  name: string;
}

export interface FeeSourceRef {
  url: string;
  title: string;
}

/** A versioned, sourced fee schedule as served by the API. */
export interface FeeSchedule extends FeeProfile {
  platform: PlatformRef;
  paymentMethod: PlatformRef;
  /** ISO 3166-1 alpha-2 marketplace region, e.g. "US". */
  region: string;
  transactionType: TransactionType;
  sellerTier: string;
  itemCategory: string;
  version: number;
  versionLabel: string;
  status: ScheduleStatus;
  verificationStatus: VerificationStatus;
  /** ISO date (YYYY-MM-DD). */
  effectiveFrom: string;
  /** ISO date (YYYY-MM-DD), exclusive; null = open-ended. */
  effectiveTo: string | null;
  /** ISO timestamp of the last human verification against the source. */
  lastVerifiedAt: string | null;
  source: FeeSourceRef | null;
  notes: string | null;
}

// ---------------------------------------------------------------------------
// Calculation input
// ---------------------------------------------------------------------------

export type SalesTaxInput =
  | { mode: "NONE" }
  /** Tax computed as a percent of the item price (and optionally buyer shipping). */
  | { mode: "RATE"; ratePercent: DecimalString; includeShipping: boolean }
  /** A known tax amount entered directly. */
  | { mode: "AMOUNT"; amount: DecimalString };

/** A user-entered replacement for all profile rules of one category. */
export interface ManualFeeOverride {
  percent?: DecimalString;
  fixedAmount?: DecimalString;
  /** Defaults to the basis of the first replaced profile rule, else item + shipping. */
  basis?: BasisComponent[];
}

export interface CalculationInput {
  currency: CurrencyCode;
  /** Item sale price paid by the buyer (excluding shipping and tax). */
  salePrice: DecimalString;
  /** Shipping & handling charged to the buyer. */
  shippingCharged: DecimalString;
  salesTax: SalesTaxInput;
  /** What the seller paid to acquire the item (cost of goods). */
  itemCost: DecimalString;
  /** Postage/label actually paid by the seller. */
  shippingCost: DecimalString;
  packagingCost: DecimalString;
  otherExpenses: DecimalString;
  /** Store subscription or other overhead allocated to this one sale. */
  subscriptionOverhead: DecimalString;
  /** Promoted listing / ad rate in percent; omitted or "0" for none. */
  promotedListingPercent?: DecimalString;
  /** Overrides the profile's tax handling for this sale. */
  taxCollectedBy?: TaxCollector;
  overrides?: {
    marketplace?: ManualFeeOverride;
    paymentProcessing?: ManualFeeOverride;
  };
}

// ---------------------------------------------------------------------------
// Calculation result
// ---------------------------------------------------------------------------

/** Where a fee value came from — shown to the user in the breakdown. */
export type ValueSource = "PROFILE" | "MANUAL";

export interface FeeLine {
  code: string;
  label: string;
  category: FeeCategory;
  source: ValueSource;
  basis: BasisComponent[];
  basisAmount: DecimalString;
  /** Percent portion of the fee before rounding/min/max, rounded to cents. */
  percentPortion: DecimalString;
  /** Fixed portion of the fee. */
  fixedPortion: DecimalString;
  /** Final fee charged (rounded, after min/max). */
  amount: DecimalString;
  /** Human-readable formula, e.g. "13.6% × $112.50 + $0.40". */
  explanation: string;
}

export type ExpenseCode = "SHIPPING_COST" | "PACKAGING" | "OTHER" | "SUBSCRIPTION";

export interface ExpenseLine {
  code: ExpenseCode;
  label: string;
  amount: DecimalString;
}

export interface CalculationResult {
  currency: CurrencyCode;
  formulaVersion: string;
  profileId: string | null;
  taxCollectedBy: TaxCollector;
  revenue: {
    itemPrice: DecimalString;
    shippingCharged: DecimalString;
    /** Item price + shipping charged: the seller's revenue. Excludes tax. */
    sellerRevenue: DecimalString;
    salesTax: DecimalString;
    /** Everything the buyer pays: item + shipping + tax. */
    buyerTotal: DecimalString;
  };
  fees: FeeLine[];
  totalFees: DecimalString;
  expenses: ExpenseLine[];
  totalExpenses: DecimalString;
  itemCost: DecimalString;
  /** Amount deposited to the seller by the platform / payment provider. */
  netPayout: DecimalString;
  /** Tax included in the payout that the seller must remit (SELLER collection only). */
  taxLiability: DecimalString;
  /** sellerRevenue − fees − expenses − itemCost. Can be negative. */
  netProfit: DecimalString;
  /** netProfit ÷ sellerRevenue × 100; null when revenue is zero. */
  profitMarginPercent: DecimalString | null;
  /** netProfit ÷ itemCost × 100; null when item cost is zero. */
  roiPercent: DecimalString | null;
  /** totalFees ÷ sellerRevenue × 100; null when revenue is zero. */
  effectiveFeePercent: DecimalString | null;
  /** Lowest sale price (other inputs held constant) with netProfit ≥ 0; null if unreachable. */
  breakEvenPrice: DecimalString | null;
  warnings: string[];
}
